import axios from 'axios';

// API Base URL — configurable via VITE_API_URL with a safe production fallback.
export const API_BASE_URL =
    (import.meta.env?.VITE_API_URL as string | undefined) ??
    'https://api.trbd.net/api';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Clear token and redirect to login
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }

        if (error.response?.status === 403) {
            // Handle forbidden access - user lacks permission
            // Import toast dynamically to avoid circular dependencies
            import('@/components/ui/sonner').then(({ toast }) => {
                toast.error('ليس لديك صلاحية للوصول إلى هذه الصفحة');
            });
        }

        return Promise.reject(error);
    }
);

export default api;

// ============ API Types ============

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    error?: {
        code: string;
        message: string;
    };
}

export interface PaginatedResponse<T> {
    items: T[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// ============ User & Auth Types ============

export interface User {
    id: string;
    email: string;
    fullName: string;
    roles: ('admin' | 'partner' | 'employee')[];  // Changed from single role to array
    createdAt: string;
}

export interface LoginRequest {
    email: string;
    password: string;
}

export interface LoginResponse {
    user: User;
    token: string;
    expiresIn: number;
}

export interface RegisterRequest {
    email: string;
    password: string;
    fullName: string;
}

export interface RegisterResponse {
    user: User;
    token: string;
}

// ============ Contact Request Types ============

export interface ContactRequest {
    id: string;
    name: string;
    email: string;
    service: string;
    message: string;
    status: 'new' | 'read' | 'contacted' | 'junk';
    createdAt: string;
}

// ============ Blog Types (SEO Optimized) ============

export interface BlogAuthor {
    id: string;
    name: string;
    email?: string;
    role?: string;
    avatar?: string;
    bio?: string;
    postCount?: number;
}

export interface BlogCategory {
    id: string;
    name: string;
    slug: string;
    description?: string;
    postCount?: number;
}

export interface BlogPost {
    id: string;
    slug: string; // SEO-friendly URL slug
    title: string;
    titleEn?: string | null;
    excerpt: string;
    excerptEn?: string | null;
    content: string; // Markdown or HTML
    contentEn?: string | null;
    featuredImage: string;
    category: BlogCategory;
    author: BlogAuthor;
    tags: string[];
    readTime: number; // in minutes
    status: 'draft' | 'pending' | 'published' | 'rejected';
    featured: boolean;
    // SEO Meta
    metaTitle?: string;
    metaTitleEn?: string | null;
    metaDescription?: string;
    metaDescriptionEn?: string | null;
    metaKeywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
    // Engagement
    views: number;
    likes: number;
    // Timestamps
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface BlogPostSummary {
    id: string;
    slug: string;
    title: string;
    excerpt: string;
    featuredImage: string;
    category: BlogCategory;
    author: { id: string; name: string; avatar?: string };
    readTime: number;
    featured: boolean;
    publishedAt: string;
    views: number;
    likes: number;
}

export interface CreateBlogPostRequest {
    title: string;
    excerpt: string;
    content: string;
    categoryId: string;
    tags?: string[];
    featuredImage?: string;
    // SEO
    metaTitle?: string;
    metaDescription?: string;
    metaKeywords?: string[];
    // Submission info
    authorName: string;
    authorEmail: string;
}

export interface BlogFilters {
    category?: string;
    tag?: string;
    author?: string;
    search?: string;
    featured?: boolean;
    page?: number;
    limit?: number;
    lang?: 'ar' | 'en';
}


// ============ Client Types ============

export interface Client {
    id: string;
    name: string;
    company: string;
    email: string;
    phone: string;
    location: string;
    notes?: string;
    projectsCount: number;
    totalRevenue: number;
    createdAt: string;
}

export interface CreateClientRequest {
    name: string;
    company?: string;
    email: string;
    phone?: string;
    location?: string;
    notes?: string;
}

// ============ Project Types ============

export interface Project {
    id: string;
    name: string;
    client: {
        id: string;
        name: string;
    };
    type: "website" | "app" | "system";
    status: "active" | "pending" | "completed" | "cancelled";
    startDate: string;
    dueDate: string;
    totalCost: number;
    paidAmount: number;
    progress: number;
    needsHosting: boolean;
    needsDomain: boolean;
    hasMaintenance: boolean;
    description?: string;
}

export interface CreateProjectRequest {
    name: string;
    clientId: string;
    type: "website" | "app" | "system";
    startDate: string;
    dueDate: string;
    totalCost: number;
    needsHosting?: boolean;
    needsDomain?: boolean;
    hasMaintenance?: boolean;
    description?: string;
}

// ============ Invoice Types ============

export interface Invoice {
    id: string;
    invoiceNumber: string;
    client: {
        id: string;
        name: string;
    };
    project?: {
        id: string;
        name: string;
    };
    amount: number;
    dueDate: string;
    status: "draft" | "sent" | "paid" | "overdue" | "cancelled";
    isOverdue: boolean;
    createdAt: string;
}

// ============ Quotation Types ============

export interface QuotationItem {
    description: string;
    quantity: number;
    unitPrice: number;
}

export interface Quotation {
    id: string;
    quotationNumber: string;
    title: string;  // إضافة جديدة
    client: {
        id: string;
        name: string;
        company?: string;
    };
    project?: {
        id: string;
        name: string;
    };
    items: QuotationItem[];
    subtotal: number;     // إضافة جديدة
    taxAmount: number;    // إضافة جديدة
    total: number;        // تغيير من totalAmount
    totalAmount: number;  // للتوافق مع الكود القديم
    validUntil: string;
    status: "draft" | "sent" | "accepted" | "rejected" | "expired";
    notes?: string;
    createdAt: string;
}

export interface CreateQuotationRequest {
    clientId: string;
    projectId?: string;
    title: string;  // إضافة جديدة
    items: QuotationItem[];
    validUntil: string;
    notes?: string;
}

// ============ Contract Clause Types ============

export interface ContractClause {
    id: string;
    title: string;
    titleAr?: string;
    content: string;
    contentAr?: string;
    isRequired: boolean;
    isEditable: boolean;
    order: number;
}

// ============ Project Contract Types ============

export interface Contract {
    id: string;
    contractNumber: string;
    client: {
        id: string;
        name: string;
        company?: string;
        email?: string;
        phone?: string;
    };
    project?: {
        id: string;
        name: string;
        type?: string;
    };
    pricingType: "fixed" | "hourly" | "milestone";
    type: "fixed" | "hourly" | "milestone"; // للتوافق مع الكود القديم
    totalValue: number;
    value: number;
    startDate: string;
    endDate?: string;
    terms?: string;
    termsNotes?: string;
    clauses?: ContractClause[];
    status: "draft" | "active" | "completed" | "cancelled" | "expired" | "terminated";
    createdAt: string;
    updatedAt?: string;
}

export interface CreateContractRequest {
    clientId: string;
    projectId?: string;
    pricingType?: "fixed" | "hourly" | "milestone";
    type?: "fixed" | "hourly" | "milestone"; // للتوافق
    totalValue?: number;
    value?: number;
    startDate: string;
    endDate?: string;
    terms?: string;
    clauses?: ContractClause[];
    status?: "draft" | "active" | "completed" | "cancelled" | "expired" | "terminated";
}

// ============ Maintenance Contract Types ============

export interface MaintenanceContract {
    id: string;
    contractNumber: string;
    client: {
        id: string;
        name: string;
        company?: string;
        email?: string;
    };
    project?: {
        id: string;
        name: string;
    };
    monthlyFee: number;
    value: number;
    startDate: string;
    endDate: string;
    services: string[];
    terms?: string;
    clauses?: ContractClause[];
    status: "active" | "expiring" | "expired" | "cancelled";
    computedStatus?: "active" | "expiring" | "expired";
    daysUntilExpiry?: number;
    createdAt: string;
}

export interface CreateMaintenanceContractRequest {
    clientId: string;
    projectId?: string;
    monthlyFee: number;
    startDate: string;
    endDate: string;
    services: string[];
    terms?: string;
    clauses?: ContractClause[];
}

// ============ Payment Types ============

export interface Payment {
    id: string;
    paymentNumber: string;
    client: {
        id: string;
        name: string;
    };
    invoice?: {
        id: string;
        invoiceNumber: string;
    };
    project?: {
        id: string;
        name: string;
    };
    collector?: {
        id: string;
        name: string;
        color: string;
    };
    amount: number;
    paymentDate: string;
    paymentMethod: "cash" | "bank_transfer" | "credit_card" | "check" | "paypal";
    status: "pending" | "completed" | "failed" | "refunded";
    reference?: string;
    notes?: string;
    createdAt: string;
}

export interface CreatePaymentRequest {
    clientId: string;  // الآن مطلوب
    invoiceId?: string;
    projectId?: string;
    amount: number;
    paymentDate: string;
    paymentMethod: "cash" | "bank_transfer" | "credit_card" | "check" | "paypal";
    collectedBy?: string;  // Partner ID who collected the payment
    reference?: string;
    notes?: string;
}

// ============ Partner Types ============

export interface Partner {
    id: string;
    name: string;
    nameEn: string;
    location: string;
    share: number;
    collected: number;
    email: string;
    phone: string;
    color: string;
}

// ============ Dashboard Stats Types ============

export interface DashboardStats {
    totalRevenue: {
        value: number;
        trend: number;
        trendIsPositive: boolean;
    };
    activeProjects: {
        value: number;
        total: number;
        trend: number;
        trendIsPositive: boolean;
    };
    pendingInvoices: {
        value: number;
        count: number;
    };
    contractsToRenew: {
        value: number;
        daysRemaining: number;
    };
}

// ============ Finance Stats Types ============

export interface FinanceStats {
    totalRevenue: number;
    pendingInvoices: {
        amount: number;
        count: number;
    };
    collectionsThisMonth: number;
    expenses: number;
    quotations: {
        total: number;
        pending: number;
    };
    invoices: {
        total: number;
        pending: number;
    };
    projectContracts: {
        total: number;
        active: number;
    };
    maintenanceContracts: {
        total: number;
        expiring: number;
    };
    payments: {
        thisMonth: number;
        total: number;
    };
}

// ============ Settings Types ============

export interface Settings {
    company: {
        name: string;
        email: string;
        phone: string;
        website: string;
        address: string;
    };
    finance: {
        currency: string;
        taxRate: number;
        invoicePrefix: string;
    };
    notifications: {
        invoiceReminders: boolean;
        maintenanceReminders: boolean;
        deliveryReminders: boolean;
    };
}

// ============ Report Types ============

export interface MonthlyRevenue {
    month: string;
    [partnerId: string]: string | number;
}

export interface RevenuePartner {
    id: string;
    name: string;
    nameEn: string | null;
    color: string | null;
}

export interface RevenueReport {
    months: MonthlyRevenue[];
    partners: RevenuePartner[];
}

export interface ProjectsByStatus {
    name: string;
    value: number;
    color: string;
}

export interface ProjectsByType {
    type: string;
    count: number;
    revenue: number;
}

// ============ Expense Types ============

export type ExpenseType = 'hosting' | 'domain' | 'tools' | 'software' | 'other';

export interface Expense {
    id: string;
    projectId?: string;
    type: ExpenseType;
    amount: number;
    paidBy: string;
    date: string;
    description?: string;
    recurring: boolean;
    renewDate?: string;
    project?: { id: string; name: string };
    payer: { id: string; name: string; color: string };
    createdAt: string;
}

export interface CreateExpenseRequest {
    projectId?: string;
    type: ExpenseType;
    amount: number;
    paidBy: string;
    date: string;
    description?: string;
    recurring?: boolean;
    renewDate?: string;
}

export interface ExpenseSummary {
    total: number;
    byType: { type: ExpenseType; amount: number }[];
    byPartner: { partnerId: string; partnerName: string; amount: number }[];
}

// ============ Partnership Summary Types ============

export interface PartnerBalance {
    id: string;
    name: string;
    color: string;
    sharePercentage: number;
    collected: number;
    expensesPaid: number;
    theoreticalShare: number;
    netBalance: number;
}

export interface SettlementRequired {
    from: { id: string; name: string };
    to: { id: string; name: string };
    amount: number;
}

export interface PartnershipSummary {
    partners: PartnerBalance[];
    totalRevenue: number;
    totalExpenses: number;
    settlementRequired: SettlementRequired | null;
}

// ============ Settlement Types ============

export interface Settlement {
    id: string;
    fromPartner: { id: string; name: string; color: string };
    toPartner: { id: string; name: string; color: string };
    amount: number;
    date: string;
    notes?: string;
    createdAt: string;
}

export interface CreateSettlementRequest {
    fromPartnerId: string;
    toPartnerId: string;
    amount: number;
    date: string;
    notes?: string;
}

// ============ Reminder Types ============

export type ReminderType = 'invoice_due' | 'maintenance_expiry' | 'domain_renewal' | 'hosting_renewal' | 'contract_expiry';
export type ReminderStatus = 'pending' | 'sent' | 'dismissed';

export interface Reminder {
    id: string;
    type: ReminderType;
    entityId: string;
    entityType: string;
    title: string;
    dueDate: string;
    status: ReminderStatus;
    daysUntil: number;
}

// ============ API Service Functions ============

// Auth API
export const authApi = {
    login: (data: LoginRequest) =>
        api.post<ApiResponse<LoginResponse>>('/auth/login', data),

    register: (data: RegisterRequest) =>
        api.post<ApiResponse<RegisterResponse>>('/auth/register', data),

    logout: () =>
        api.post('/auth/logout'),

    getMe: () =>
        api.get<ApiResponse<User>>('/auth/me'),
};

// Clients API
export const clientsApi = {
    getAll: (params?: { search?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ clients: Client[]; pagination: PaginatedResponse<Client>['pagination'] }>>('/clients', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Client>>(`/clients/${id}`),

    create: (data: CreateClientRequest) =>
        api.post<ApiResponse<Client>>('/clients', data),

    update: (id: string, data: Partial<CreateClientRequest>) =>
        api.put<ApiResponse<Client>>(`/clients/${id}`, data),

    delete: (id: string) =>
        api.delete(`/clients/${id}`),
};

// Projects API
export const projectsApi = {
    getAll: (params?: { search?: string; status?: string; type?: string; clientId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ projects: Project[]; pagination: PaginatedResponse<Project>['pagination'] }>>('/projects', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Project>>(`/projects/${id}`),

    create: (data: CreateProjectRequest) =>
        api.post<ApiResponse<Project>>('/projects', data),

    update: (id: string, data: Partial<CreateProjectRequest>) =>
        api.put<ApiResponse<Project>>(`/projects/${id}`, data),

    updateProgress: (id: string, progress: number) =>
        api.patch<ApiResponse<Project>>(`/projects/${id}/progress`, { progress }),

    updateStatus: (id: string, status: Project['status']) =>
        api.patch<ApiResponse<Project>>(`/projects/${id}/status`, { status }),

    delete: (id: string) =>
        api.delete(`/projects/${id}`),
};

// Invoices API
export const invoicesApi = {
    getAll: (params?: { status?: string; clientId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ invoices: Invoice[]; summary: { totalPending: number; totalPaid: number; totalOverdue: number } }>>('/invoices', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Invoice>>(`/invoices/${id}`),

    create: (data: { clientId: string; projectId?: string; amount: number; dueDate: string; notes?: string }) =>
        api.post<ApiResponse<Invoice>>('/invoices', data),

    update: (id: string, data: { clientId?: string; projectId?: string; amount?: number; dueDate?: string; notes?: string; status?: string }) =>
        api.put<ApiResponse<Invoice>>(`/invoices/${id}`, data),

    send: (id: string) =>
        api.post(`/invoices/${id}/send`),

    markPaid: (id: string, data: { paymentDate: string; paymentMethod: string; reference?: string }) =>
        api.post(`/invoices/${id}/mark-paid`, data),

    delete: (id: string) =>
        api.delete(`/invoices/${id}`),
};

// Dashboard API
export const dashboardApi = {
    getStats: () =>
        api.get<ApiResponse<DashboardStats>>('/dashboard/stats'),

    getRevenueChart: (year?: number) =>
        api.get<ApiResponse<{ month: string; revenue: number }[]>>('/dashboard/revenue-chart', { params: { year } }),
};

// Finance API
export const financeApi = {
    getStats: () =>
        api.get<ApiResponse<FinanceStats>>('/finance/stats'),
};

// Quotations API
export const quotationsApi = {
    getAll: (params?: { status?: string; clientId?: string; projectId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ quotations: Quotation[]; summary: { total: number; pending: number; accepted: number } }>>('/quotations', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Quotation>>(`/quotations/${id}`),

    create: (data: CreateQuotationRequest) =>
        api.post<ApiResponse<Quotation>>('/quotations', data),

    update: (id: string, data: Partial<CreateQuotationRequest>) =>
        api.put<ApiResponse<Quotation>>(`/quotations/${id}`, data),

    delete: (id: string) =>
        api.delete(`/quotations/${id}`),

    send: (id: string) =>
        api.post(`/quotations/${id}/send`),

    updateStatus: (id: string, status: Quotation['status']) =>
        api.patch<ApiResponse<Quotation>>(`/quotations/${id}/status`, { status }),
};

// Contracts API
export const contractsApi = {
    getAll: (params?: { status?: string; clientId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ contracts: Contract[]; summary: { total: number; active: number; completed: number } }>>('/project-contracts', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Contract>>(`/project-contracts/${id}`),

    create: (data: CreateContractRequest) =>
        api.post<ApiResponse<Contract>>('/project-contracts', data),

    update: (id: string, data: Partial<CreateContractRequest>) =>
        api.put<ApiResponse<Contract>>(`/project-contracts/${id}`, data),

    delete: (id: string) =>
        api.delete(`/project-contracts/${id}`),

    updateStatus: (id: string, status: Contract['status']) =>
        api.patch<ApiResponse<Contract>>(`/project-contracts/${id}/status`, { status }),
};

// Maintenance Contracts API
export const maintenanceApi = {
    getAll: (params?: { status?: string; clientId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ contracts: MaintenanceContract[]; summary: { total: number; active: number; expiring: number } }>>('/maintenance-contracts', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<MaintenanceContract>>(`/maintenance-contracts/${id}`),

    create: (data: CreateMaintenanceContractRequest) =>
        api.post<ApiResponse<MaintenanceContract>>('/maintenance-contracts', data),

    update: (id: string, data: Partial<CreateMaintenanceContractRequest>) =>
        api.put<ApiResponse<MaintenanceContract>>(`/maintenance-contracts/${id}`, data),

    delete: (id: string) =>
        api.delete(`/maintenance-contracts/${id}`),

    renew: (id: string, data: { endDate: string; monthlyFee?: number }) =>
        api.post<ApiResponse<MaintenanceContract>>(`/maintenance-contracts/${id}/renew`, data),
};

// Payments API
export const paymentsApi = {
    getAll: (params?: { status?: string; clientId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ payments: Payment[]; summary: { totalAmount: number; thisMonth: number; pending: number } }>>('/payments', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Payment>>(`/payments/${id}`),

    create: (data: CreatePaymentRequest) =>
        api.post<ApiResponse<Payment>>('/payments', data),

    update: (id: string, data: Partial<CreatePaymentRequest>) =>
        api.put<ApiResponse<Payment>>(`/payments/${id}`, data),

    delete: (id: string) =>
        api.delete(`/payments/${id}`),

    updateStatus: (id: string, status: Payment['status']) =>
        api.patch<ApiResponse<Payment>>(`/payments/${id}/status`, { status }),
};

// Reports API
export const reportsApi = {
    getRevenue: (year?: number, partnerId?: string) =>
        api.get<ApiResponse<RevenueReport>>('/reports/revenue', { params: { year, partnerId } }),

    getProjectsByStatus: () =>
        api.get<ApiResponse<ProjectsByStatus[]>>('/reports/projects-by-status'),

    getProjectsByType: () =>
        api.get<ApiResponse<ProjectsByType[]>>('/reports/projects-by-type'),
};

// Partners API
export const partnersApi = {
    getAll: () =>
        api.get<ApiResponse<{ partners: Partner[]; totalRevenue: number }>>('/partners'),

    update: (id: string, data: Partial<Partner>) =>
        api.put<ApiResponse<Partner>>(`/partners/${id}`, data),
};

// Settings API
export const settingsApi = {
    get: () =>
        api.get<ApiResponse<Settings>>('/settings'),

    update: (data: Partial<Settings>) =>
        api.put<ApiResponse<{ message: string; updatedAt: string }>>('/settings', data),
};

// Users API (Admin Only)
export const usersApi = {
    getAll: (params?: { page?: number; limit?: number; search?: string; role?: string }) =>
        api.get<ApiResponse<{ users: User[]; pagination: PaginatedResponse<User>['pagination'] }>>('/users', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<User>>(`/users/${id}`),

    create: (data: { email: string; password: string; fullName: string; roles?: ('admin' | 'partner' | 'employee')[] }) =>
        api.post<ApiResponse<User>>('/users', data),

    update: (id: string, data: { email?: string; fullName?: string; roles?: ('admin' | 'partner' | 'employee')[]; password?: string }) =>
        api.put<ApiResponse<User>>(`/users/${id}`, data),

    delete: (id: string) =>
        api.delete(`/users/${id}`),

    changeRoles: (id: string, roles: ('admin' | 'partner' | 'employee')[]) =>
        api.patch<ApiResponse<User>>(`/users/${id}/roles`, { roles }),

    getRoles: () =>
        api.get<ApiResponse<{ roles: string[]; descriptions: Record<string, string> }>>('/users/roles'),
};

// Expenses API
export const expensesApi = {
    getAll: (params?: { type?: ExpenseType; partnerId?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ expenses: Expense[]; pagination: PaginatedResponse<Expense>['pagination'] }>>('/expenses', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Expense>>(`/expenses/${id}`),

    create: (data: CreateExpenseRequest) =>
        api.post<ApiResponse<Expense>>('/expenses', data),

    update: (id: string, data: Partial<CreateExpenseRequest>) =>
        api.put<ApiResponse<Expense>>(`/expenses/${id}`, data),

    delete: (id: string) =>
        api.delete(`/expenses/${id}`),

    getSummary: () =>
        api.get<ApiResponse<ExpenseSummary>>('/expenses/summary'),
};

// Partnership API
export const partnershipApi = {
    getSummary: () =>
        api.get<ApiResponse<PartnershipSummary>>('/partners/summary'),
};

// Settlements API
export const settlementsApi = {
    getAll: (params?: { page?: number; limit?: number }) =>
        api.get<ApiResponse<{ settlements: Settlement[]; pagination: PaginatedResponse<Settlement>['pagination'] }>>('/settlements', { params }),

    create: (data: CreateSettlementRequest) =>
        api.post<ApiResponse<Settlement>>('/settlements', data),

    update: (id: string, data: Partial<CreateSettlementRequest>) =>
        api.patch<ApiResponse<Settlement>>(`/settlements/${id}`, data),

    delete: (id: string) =>
        api.delete(`/settlements/${id}`),
};

// Reminders API
export const remindersApi = {
    getAll: (params?: { type?: ReminderType; status?: ReminderStatus }) =>
        api.get<ApiResponse<{ reminders: Reminder[] }>>('/reminders', { params }),

    getUpcoming: () =>
        api.get<ApiResponse<Reminder[]>>('/reminders/upcoming'),

    dismiss: (id: string) =>
        api.patch(`/reminders/${id}/dismiss`),

    restore: (id: string) =>
        api.patch(`/reminders/${id}/restore`),

    regenerate: () =>
        api.post<ApiResponse<unknown>>('/reminders/regenerate'),
};

// ============ Domain Types ============

export interface Domain {
    id: string;
    name: string;
    registrar: string;
    projectId: string;
    project: {
        id: string;
        name: string;
    };
    registrationDate: string;
    expiryDate: string;
    autoRenew: boolean;
    cost: number;
    status: "active" | "expiring" | "expired";
    daysUntilExpiry: number;
    notes?: string;
    createdAt: string;
}

export interface CreateDomainRequest {
    name: string;
    registrar: string;
    projectId: string;
    registrationDate: string;
    expiryDate: string;
    autoRenew?: boolean;
    cost: number;
    notes?: string;
}

// ============ Hosting Types ============

export interface Hosting {
    id: string;
    provider: string;
    plan: string;
    projectId: string;
    project: {
        id: string;
        name: string;
    };
    serverIp?: string;
    startDate: string;
    expiryDate: string;
    monthlyFee: number;
    storage?: string;
    bandwidth?: string;
    ssl: boolean;
    status: "active" | "expiring" | "expired";
    daysUntilExpiry: number;
    notes?: string;
    createdAt: string;
}

export interface CreateHostingRequest {
    provider: string;
    plan: string;
    projectId: string;
    serverIp?: string;
    startDate: string;
    expiryDate: string;
    monthlyFee: number;
    storage?: string;
    bandwidth?: string;
    ssl?: boolean;
    notes?: string;
}

// ============ Domains API ============

export const domainsApi = {
    getAll: (params?: { projectId?: string; status?: string; search?: string }) =>
        api.get<ApiResponse<{ domains: Domain[]; summary: { total: number; active: number; expiring: number; expired: number } }>>('/domains', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Domain>>(`/domains/${id}`),

    create: (data: CreateDomainRequest) =>
        api.post<ApiResponse<Domain>>('/domains', data),

    update: (id: string, data: Partial<CreateDomainRequest>) =>
        api.put<ApiResponse<Domain>>(`/domains/${id}`, data),

    delete: (id: string) =>
        api.delete(`/domains/${id}`),

    getExpiring: () =>
        api.get<ApiResponse<Domain[]>>('/domains/expiring'),
};

// ============ Hosting API ============

export const hostingApi = {
    getAll: (params?: { projectId?: string; status?: string; search?: string }) =>
        api.get<ApiResponse<{ hostings: Hosting[]; summary: { total: number; active: number; expiring: number; expired: number } }>>('/hosting', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<Hosting>>(`/hosting/${id}`),

    create: (data: CreateHostingRequest) =>
        api.post<ApiResponse<Hosting>>('/hosting', data),

    update: (id: string, data: Partial<CreateHostingRequest>) =>
        api.put<ApiResponse<Hosting>>(`/hosting/${id}`, data),

    delete: (id: string) =>
        api.delete(`/hosting/${id}`),

    getExpiring: () =>
        api.get<ApiResponse<Hosting[]>>('/hosting/expiring'),
};

// ============ PDF API ============

// Helper function for downloading files
const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

export const pdfApi = {
    downloadQuotation: async (id: string, quotationNumber: string) => {
        const response = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
        downloadFile(response.data, `عرض-سعر-${quotationNumber}.pdf`);
    },

    downloadInvoice: async (id: string, invoiceNumber: string) => {
        const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
        downloadFile(response.data, `فاتورة-${invoiceNumber}.pdf`);
    },

    downloadProjectContract: async (id: string, contractNumber: string) => {
        const response = await api.get(`/project-contracts/${id}/pdf`, { responseType: 'blob' });
        downloadFile(response.data, `عقد-مشروع-${contractNumber}.pdf`);
    },

    downloadMaintenanceContract: async (id: string, contractNumber: string) => {
        const response = await api.get(`/maintenance-contracts/${id}/pdf`, { responseType: 'blob' });
        downloadFile(response.data, `عقد-صيانة-${contractNumber}.pdf`);
    },

    downloadPaymentReceipt: async (id: string, paymentNumber: string) => {
        const response = await api.get(`/payments/${id}/pdf`, { responseType: 'blob' });
        downloadFile(response.data, `إيصال-دفع-${paymentNumber}.pdf`);
    },
};

// ============ Contact Requests API ============

export const contactRequestsApi = {
    submit: (data: { name: string; email: string; service: string; message: string }) =>
        api.post<ApiResponse<ContactRequest>>('/contact-requests', data),

    getAll: (params?: { status?: string; search?: string; page?: number; limit?: number }) =>
        api.get<ApiResponse<{ leads: ContactRequest[]; pagination: PaginatedResponse<ContactRequest>['pagination'] }>>('/contact-requests', { params }),

    getById: (id: string) =>
        api.get<ApiResponse<ContactRequest>>(`/contact-requests/${id}`),

    updateStatus: (id: string, status: ContactRequest['status']) =>
        api.patch<ApiResponse<ContactRequest>>(`/contact-requests/${id}/status`, { status }),

    delete: (id: string) =>
        api.delete(`/contact-requests/${id}`),
};

// ============ Blog API ============

export const blogApi = {
    // Public endpoints (no auth required)
    getPosts: (params?: BlogFilters) =>
        api.get<ApiResponse<{
            posts: BlogPostSummary[];
            pagination: PaginatedResponse<BlogPostSummary>['pagination'];
            featuredPost?: BlogPostSummary;
        }>>('/blog/posts', { params }),

    getPostBySlug: (slug: string, opts?: { lang?: 'ar' | 'en' }) =>
        api.get<ApiResponse<BlogPost>>(`/blog/posts/${slug}`, { params: { lang: opts?.lang } }),

    getCategories: (opts?: { lang?: 'ar' | 'en' }) =>
        api.get<ApiResponse<{ categories: BlogCategory[] }>>('/blog/categories', { params: { lang: opts?.lang } }),

    getTags: () =>
        api.get<ApiResponse<{ tags: string[] }>>('/blog/tags'),

    getRelatedPosts: (postId: string, opts?: { limit?: number; lang?: 'ar' | 'en' }) =>
        api.get<ApiResponse<{ posts: BlogPostSummary[] }>>(`/blog/posts/${postId}/related`, {
            params: { limit: opts?.limit, lang: opts?.lang },
        }),

    // Public submission (visitors can submit)
    submitPost: (data: CreateBlogPostRequest) =>
        api.post<ApiResponse<{ message: string; postId: string }>>('/blog/posts/submit', data),

    // Engagement (no auth, uses fingerprint/IP)
    likePost: (postId: string) =>
        api.post<ApiResponse<{ likes: number }>>(`/blog/posts/${postId}/like`),

    incrementViews: (postId: string) =>
        api.post<ApiResponse<{ views: number }>>(`/blog/posts/${postId}/view`),

    // Admin endpoints (auth required)
    getAllPosts: (params?: BlogFilters & { status?: BlogPost['status'] }) =>
        api.get<ApiResponse<{
            posts: BlogPost[];
            pagination: PaginatedResponse<BlogPost>['pagination'];
            stats: { total: number; published: number; pending: number; draft: number; rejected: number }
        }>>('/blog/admin/posts', { params }),

    getPostById: (id: string) =>
        api.get<ApiResponse<BlogPost>>(`/blog/admin/posts/${id}`),

    createPost: (data: Partial<BlogPost>) =>
        api.post<ApiResponse<BlogPost>>('/blog/admin/posts', data),

    updatePost: (id: string, data: Partial<BlogPost>) =>
        api.put<ApiResponse<BlogPost>>(`/blog/admin/posts/${id}`, data),

    deletePost: (id: string) =>
        api.delete(`/blog/admin/posts/${id}`),

    updatePostStatus: (id: string, status: BlogPost['status']) =>
        api.patch<ApiResponse<BlogPost>>(`/blog/admin/posts/${id}/status`, { status }),

    toggleFeatured: (id: string) =>
        api.patch<ApiResponse<BlogPost>>(`/blog/admin/posts/${id}/featured`),

    // Category management (admin)
    createCategory: (data: { name: string; slug: string; description?: string }) =>
        api.post<ApiResponse<BlogCategory>>('/blog/admin/categories', data),

    updateCategory: (id: string, data: Partial<BlogCategory>) =>
        api.put<ApiResponse<BlogCategory>>(`/blog/admin/categories/${id}`, data),

    deleteCategory: (id: string) =>
        api.delete(`/blog/admin/categories/${id}`),

    // Author management (admin)
    getAuthors: () =>
        api.get<ApiResponse<{ authors: BlogAuthor[] }>>('/blog/admin/authors'),

    createAuthor: (data: Partial<BlogAuthor>) =>
        api.post<ApiResponse<BlogAuthor>>('/blog/admin/authors', data),

    updateAuthor: (id: string, data: Partial<BlogAuthor>) =>
        api.put<ApiResponse<BlogAuthor>>(`/blog/admin/authors/${id}`, data),
};

// ============ Chatbot Types ============

export interface ChatSession {
    sessionId: string;
    message: string;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
}

export interface ChatConversation {
    id: string;
    sessionId: string;
    platform: 'telegram' | 'web';
    status: 'active' | 'ended' | 'archived';
    telegramChatId?: string;
    telegramUsername?: string;
    telegramFirstName?: string;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    projectInterest?: string;
    messageCount: number;
    createdAt: string;
    updatedAt: string;
    lastMessageAt?: string | null;
    messages?: ChatMessage[];
}

// ============ Chatbot API ============

export const chatbotApi = {
    // Public endpoints (no auth required)
    start: (phone: string) =>
        api.post<ApiResponse<ChatSession>>('/chatbot/start', { phone }),

    sendMessage: (sessionId: string, message: string) =>
        api.post<ApiResponse<{ response: string; sessionId: string }>>('/chatbot/message', { sessionId, message }),

    end: (sessionId: string) =>
        api.post<ApiResponse<{ message: string }>>('/chatbot/end', { sessionId }),

    getInfo: () =>
        api.get<ApiResponse<{
            company: {
                nameAr: string;
                nameEn: string;
                location: string;
                phone: string;
                whatsapp: string;
                whatsappLink: string;
                website: string;
                manager: string;
            };
            services: Array<{
                id: string;
                nameAr: string;
                types: Array<{ name: string; priceRange: { min: number; max: number } }>;
            }>;
            previousWork: Array<{ name: string; type: string; url: string }>;
        }>>('/chatbot/info'),

    // Admin endpoints (auth required)
    getConversations: (params?: { platform?: string; status?: string; startDate?: string }) =>
        api.get<ApiResponse<{ count: number; conversations: ChatConversation[] }>>('/chatbot/admin/conversations', { params }),

    getConversation: (id: string) =>
        api.get<ApiResponse<ChatConversation>>(`/chatbot/admin/conversations/${id}`),

    archiveConversation: (id: string) =>
        api.patch<ApiResponse<{ message: string }>>(`/chatbot/admin/conversations/${id}/archive`),

    sendReply: (id: string, content: string) =>
        api.post<ApiResponse<ChatMessage>>(`/chatbot/admin/conversations/${id}/reply`, { content }),
};

// ============ Simple Mode Chatbot Types ============

export type SimpleChatStage =
    | 'MAIN_MENU'
    | 'SELECT_PROJECT_TYPE'
    | 'ADD_DETAILS_CONFIRM'
    | 'SELECT_FEATURES'
    | 'ENTER_PHONE'
    | 'SELECT_BUDGET'
    | 'COMPLETED'
    | 'SERVICES_INFO'
    | 'PORTFOLIO_INFO'
    | 'CONTACT_MANAGER'
    | 'FREE_CHAT'
    | 'MEET_FOUNDER'
    | string; // allow new backend stages without type rebuild

export interface SimpleChatButton {
    text: string;
    action: string;
    url?: string | null;
    selected?: boolean; // For toggle buttons in SELECT_FEATURES
}

export interface SimpleChatSession {
    sessionId: string;
    stage: SimpleChatStage;
    collectedData: {
        projectType?: 'website' | 'mobile_app' | 'system';
        features?: string[];
        customDescription?: string;
        budget?: string;
        phone?: string;
        name?: string;
        email?: string;
    };
    createdAt: string;
    updatedAt: string;
}

export interface SimpleChatResponse {
    sessionId: string;
    stage: SimpleChatStage;
    message: string;
    buttons: SimpleChatButton[];
    expectingText?: boolean;
    inputPlaceholder?: string;
    validationPattern?: string;
    errorMessage?: string;
}

// ============ Simple Mode Chatbot API ============

export type SimpleChatStreamEvent =
    | { type: 'chunk'; delta: string }
    | {
        type: 'done';
        stage: SimpleChatStage;
        message: string;
        buttons: SimpleChatButton[];
        expectingText?: boolean;
        inputPlaceholder?: string;
        completed?: boolean;
    }
    | { type: 'error'; message: string };

/**
 * Stream a Simple Mode reply via fetch + ReadableStream. Browsers' EventSource
 * is GET-only, so we POST and parse SSE manually.
 *
 * `onEvent` fires for each parsed event (chunk → done). The promise resolves
 * after the stream closes. If `signal` aborts mid-flight the reader is
 * cancelled and the promise rejects with the abort reason.
 */
const streamSimpleChatMessage = async (
    sessionId: string,
    message: string,
    onEvent: (event: SimpleChatStreamEvent) => void,
    signal?: AbortSignal,
): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/chatbot/web/message/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId, message }),
        signal,
    });

    if (!response.ok || !response.body) {
        throw new Error(`Stream request failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by a blank line. Each frame can have
            // multiple `data:` lines that should be joined with '\n'.
            let sepIndex: number;
            while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
                const frame = buffer.slice(0, sepIndex);
                buffer = buffer.slice(sepIndex + 2);

                const dataLines = frame
                    .split('\n')
                    .filter((line) => line.startsWith('data:'))
                    .map((line) => line.slice(5).trimStart());

                if (dataLines.length === 0) continue;

                try {
                    const payload = JSON.parse(dataLines.join('\n')) as SimpleChatStreamEvent;
                    onEvent(payload);
                } catch (err) {
                    console.warn('Failed to parse SSE frame:', err);
                }
            }
        }
    } finally {
        try { reader.releaseLock(); } catch (_e) { /* noop */ }
    }
};

export const simpleChatApi = {
    // Start new session
    start: (name?: string, email?: string) =>
        api.post<ApiResponse<SimpleChatResponse>>('/chatbot/web/start', { name, email }),

    // Handle button action
    action: (sessionId: string, action: string) =>
        api.post<ApiResponse<SimpleChatResponse>>('/chatbot/web/action', { sessionId, action }),

    // Handle text input (phone or custom description)
    message: (sessionId: string, message: string) =>
        api.post<ApiResponse<SimpleChatResponse>>('/chatbot/web/message', { sessionId, message }),

    // Stream the assistant reply token-by-token. Falls back to .message() at
    // call sites that need axios-style error handling.
    messageStream: streamSimpleChatMessage,

    // Get session state
    getSession: (sessionId: string) =>
        api.get<ApiResponse<SimpleChatSession>>(`/chatbot/web/session/${sessionId}`),
};
